import { IconAlertCircle, IconFeather } from '@tabler/icons-react';

import React, { useCallback, useState } from 'react';

import {
    Alert,
    Button,
    Group,
    LoadingOverlay,
    Modal,
    Select,
    Stack,
    Text,
    Textarea
} from '@mantine/core';

import { GenerateMermaidResponse } from '../../../api/models';
import { usePostApiOpenAIGenerateMermaid } from '../../../api/open-a-i/open-a-i';

interface AiMermaidGeneratorModalProps {
    opened: boolean;
    onClose: () => void;
    onGenerate: (mermaidCode: string) => void;
}

const MERMAID_DIAGRAM_TYPES = [
    { value: 'flowchart', label: 'Flowchart' },
    { value: 'sequence', label: 'Sequence Diagram' },
    { value: 'class', label: 'Class Diagram' },
    { value: 'state', label: 'State Diagram' },
    { value: 'entity-relationship', label: 'Entity Relationship Diagram' },
    { value: 'gantt', label: 'Gantt Chart' },
    { value: 'pie', label: 'Pie Chart' },
    { value: 'journey', label: 'User Journey' },
    { value: 'gitgraph', label: 'Git Graph' },
    { value: 'mindmap', label: 'Mind Map' },
    { value: 'timeline', label: 'Timeline' }
];

export const AiMermaidGeneratorModal: React.FC<
    AiMermaidGeneratorModalProps
> = ({ opened, onClose, onGenerate }) => {
    const [diagramType, setDiagramType] = useState<string>('flowchart');
    const [description, setDescription] = useState('');
    const [error, setError] = useState<string | null>(null);

    const { mutate: generateMermaid, isPending } =
        usePostApiOpenAIGenerateMermaid({
            mutation: {
                onSuccess: (data: GenerateMermaidResponse) => {
                    if (data?.success && data?.mermaidCode) {
                        onGenerate(data.mermaidCode);
                        handleClose();
                    } else {
                        setError(data?.error ?? 'Failed to generate diagram');
                    }
                },
                onError: error => {
                    setError(error.message || 'Failed to generate diagram');
                }
            }
        });

    const handleClose = useCallback(() => {
        setDescription('');
        setDiagramType('flowchart');
        setError(null);
        onClose();
    }, [onClose]);

    const handleGenerate = useCallback(() => {
        if (!description.trim()) {
            setError('Please provide a description for the diagram');
            return;
        }

        setError(null);
        generateMermaid({
            data: {
                diagramType,
                description: description.trim()
            }
        });
    }, [diagramType, description, generateMermaid]);

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={
                <Group>
                    <IconFeather size={24} />
                    <Text fw={600}>AI Mermaid Generator</Text>
                </Group>
            }
            size='md'
            centered
        >
            <div style={{ position: 'relative' }}>
                <LoadingOverlay visible={isPending} />

                <Stack gap='md'>
                    <Select
                        label='Diagram Type'
                        placeholder='Select diagram type'
                        data={MERMAID_DIAGRAM_TYPES}
                        value={diagramType}
                        onChange={value => setDiagramType(value ?? 'flowchart')}
                        required
                    />

                    <Textarea
                        resize='vertical'
                        label='Description'
                        placeholder="Describe the diagram you want to create... For example: 'Create a flowchart showing the user registration process with email verification'"
                        value={description}
                        onChange={event => setDescription(event.target.value)}
                        minRows={4}
                        maxRows={12}
                        required
                    />

                    {error && (
                        <Alert color='red' icon={<IconAlertCircle size={16} />}>
                            {error}
                        </Alert>
                    )}

                    <Group justify='flex-end' gap='sm'>
                        <Button
                            variant='subtle'
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            leftSection={<IconFeather size={16} />}
                            onClick={handleGenerate}
                            loading={isPending}
                            disabled={!description.trim()}
                        >
                            Generate Diagram
                        </Button>
                    </Group>
                </Stack>
            </div>
        </Modal>
    );
};
